import { redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { ONBOARDING_REQUIRED_USER_SOURCES } from '../../../config';
import featureConfig, { getSingleActiveProjectId, IFeatureConfig } from '../../../config/featureConfig';
import { LOGIN_REDIRECT_KEY } from '../../../pages/Lander/useAuthRedirect';
import { ROUTE } from '../../../routes';
import { RouteGuard, RouterContext } from './types';

const NON_REDIRECTABLE_ROUTES = [ROUTE.LOGOUT, ROUTE.PAGE_NOT_FOUND, ROUTE.LOGIN_SIGNUP_CHOICE, ROUTE.SIGNUP];

const validateEmail = (email: string): boolean => {
  try {
    const emailSchema = z.string().email();
    const parsedEmail = emailSchema.safeParse(email);
    return parsedEmail.success;
  } catch {
    return false;
  }
};

export const redirectUnauthenticated: RouteGuard = ({ context, location }) => {
  if (!context.isAuthenticated) {
    const pathname = location?.pathname as ROUTE;
    const href = location?.href;

    if (href && !NON_REDIRECTABLE_ROUTES.includes(pathname)) {
      window.localStorage.setItem(LOGIN_REDIRECT_KEY, href);

      const searchParams = href ? new URL(href, window.location.origin).searchParams : null;
      const email = searchParams?.get('email') || undefined;

      const isEmailValid = email ? validateEmail(email) : false;

      throw redirect({
        to: '/auth',
        search: {
          flow: 'login',
          ...(isEmailValid ? { email } : {}),
        },
      });
    }

    throw redirect({
      to: '/login-signup-choice',
    });
  }
};

export const redirectAuthenticated: RouteGuard = ({ context, location }) => {
  if (context.isAuthenticated) {
    const searchParams = location?.href ? new URL(location.href, window.location.origin).searchParams : null;
    const redirectTo = searchParams?.get('redirect');

    const needsOnboarding =
      !context.user?.propertyIds?.[0] &&
      !!context.user?.source &&
      ONBOARDING_REQUIRED_USER_SOURCES.has(context.user.source);

    if (needsOnboarding) {
      throw redirect({
        to: ROUTE.ONBOARDING,
        search: (prev) => ({ ...prev, ...(redirectTo ? { redirect: redirectTo } : {}) }),
      });
    }

    if (redirectTo?.startsWith('/')) {
      throw redirect({ to: redirectTo });
    }

    throw redirect({ to: '/' });
  }
};

export const featureFlagGuard = (featureConfig: IFeatureConfig, redirectDestination?: string): RouteGuard => {
  const redirectFeatureFlagDisabled: RouteGuard = async ({ context }) => {
    const { user, property, flags } = context;

    if (!featureConfig(user, flags, property)) {
      throw redirect({
        to: redirectDestination || '/404',
      });
    }
  };
  return redirectFeatureFlagDisabled;
};

export const redirectNonOnboardingUsers: RouteGuard = ({ context }) => {
  if (!context.user?.settings?.shouldSeeProjectOnboarding) {
    throw redirect({
      to: '/',
    });
  }
};

export const redirectOnboardingUsersToWelcome: RouteGuard = ({ context }) => {
  if (context.user?.settings?.shouldSeeProjectOnboarding) {
    throw redirect({
      to: '/welcome',
    });
  }
};

export const redirectSingleActiveProjectToDetail: RouteGuard = ({ context }) => {
  const { user, flags } = context;
  if (!featureConfig.displayProjectAsHome(user, flags)) return;

  const projectId = getSingleActiveProjectId(user);
  if (!projectId) return;

  throw redirect({ to: ROUTE.PROJECT_DETAIL, params: { id: projectId } });
};

export const redirectProjectDetailToHomeV2 = ({
  context,
  params,
  search,
}: {
  context: RouterContext;
  params: { id: string };
  search: Record<string, unknown>;
}) => {
  if (!featureConfig.displayHomePageV2(context.user, context.flags)) return;

  throw redirect({ to: '/home/projects/$id', params: { id: params.id }, search });
};

export const redirectEnrollmentWithoutPlanToChoosePlan = ({
  search,
}: {
  search: {
    planId?: string;
    plan?: 'recommended';
    scope?: string;
    returnUrl?: string;
    context?: Record<string, unknown>;
  };
}) => {
  const { planId, plan, scope = 'enrollment', returnUrl, context } = search;
  if (scope !== 'enrollment' || planId || plan === 'recommended') return;

  throw redirect({ to: '/utility-rate-plan-choose-plan', search: { returnUrl, context }, replace: true });
};
